import { ApiLinkEntity, ApiLinkRel } from "config/constants"
import i18n from "intl/i18n"
import dayjs, { Dayjs } from "utils/dayjs"

import { Guid, IApiLink } from "@/types"
import RequestLocation, { IGetRequestLocationDto, RequestLocationType } from "./RequestLocation"
import ShippingOrderAsset, { IGetShippingOrderAssetDto } from "./ShippingOrderAsset"
import ShippingOrderTransport, { IGetShippingOrderTransportDto } from "./ShippingOrderTransport"
import Unit from "./Unit"

export enum ShippingOrderStatus {
    pending = "pending",
    forwardedToShipping = "forwardedToShipping",
    pushedToStock = "pushedToStock",
    inProgress = "inProgress",
    completed = "completed",
}

export enum ShippingOrderDisplayStatus {
    loadingListNotCreated = "loadingListNotCreated",
    loadingListInProgress = "loadingListInProgress",
    pushedToStock = "pushedToStock",
    completed = "completed",
}

export enum ShippingOrdersSortBy {
    number = "number",
    departureDate = "departureDate",
    arrivalDate = "arrivalDate",
    customerName = "customerName",
}

export enum ShippingOrderType {
    delivery = "delivery",
    return = "return",
}

export interface IHasTransportSegmentLoadedAssetsParams {
    transportId: Guid
    departureLocationId: Guid
    arrivalLocationId: Guid
}

export interface IGetShippingOrderDto {
    id: Guid
    number: string
    reservationId: Guid
    status: ShippingOrderStatus
    type: ShippingOrderType
    note?: string | null
    deliveryTermId?: Guid | null
    responsibleWarehouseId?: Guid | null
    departureDate?: string | null
    arrivalDate?: string | null
    departureDateMin?: string | null
    arrivalDateMax?: string | null
    personInCharge?: string | null
    personInChargePhone?: string | null
    forwardToShippingDate?: string | null
    pushToStockDate?: string | null
    completeDate?: string | null
    completionReportUri?: string | null
    assets?: IGetShippingOrderAssetDto[] | null
    transports?: IGetShippingOrderTransportDto[] | null
    locations?: IGetRequestLocationDto[] | null
    deliveryNoteHeaderText?: string | null
    deliveryNoteFooterText?: string | null
    deliveryNotePrintFeatures: boolean
    links: IApiLink[]
}

export default class ShippingOrder {
    id: Guid
    number: string
    reservationId: Guid
    status: ShippingOrderStatus
    type: ShippingOrderType
    note?: string
    deliveryTermId?: Guid | null
    responsibleWarehouseId?: Guid | null
    departureDate?: Dayjs
    arrivalDate?: Dayjs
    departureDateMin?: Dayjs
    arrivalDateMax?: Dayjs
    personInCharge?: string
    personInChargePhone?: string
    forwardToShippingDate?: Dayjs
    pushToStockDate?: Dayjs
    completeDate?: Dayjs
    completionReportUri?: string | null
    assets: ShippingOrderAsset[]
    transports: ShippingOrderTransport[]
    locations: RequestLocation[]
    eventLocation?: RequestLocation
    deliveryNoteHeaderText?: string
    deliveryNoteFooterText?: string
    deliveryNotePrintFeatures: boolean

    linkDelete?: IApiLink

    constructor({
        dto,
        sizeUnits,
    }: {
        dto: IGetShippingOrderDto
        sizeUnits?: Unit[]
    }) {
        this.id = dto.id
        this.number = dto.number
        this.reservationId = dto.reservationId
        this.status = dto.status
        this.type = dto.type
        this.note = dto.note ?? undefined
        this.deliveryTermId = dto.deliveryTermId
        this.responsibleWarehouseId = dto.responsibleWarehouseId
        this.departureDate = dto.departureDate ? dayjs.utc(dto.departureDate) : undefined
        this.arrivalDate = dto.arrivalDate ? dayjs.utc(dto.arrivalDate) : undefined
        this.departureDateMin = dto.departureDateMin ? dayjs.utc(dto.departureDateMin) : undefined
        this.arrivalDateMax = dto.arrivalDateMax ? dayjs.utc(dto.arrivalDateMax) : undefined
        this.personInCharge = dto.personInCharge ?? undefined
        this.personInChargePhone = dto.personInChargePhone ?? undefined
        this.forwardToShippingDate = dto.forwardToShippingDate ? dayjs(dto.forwardToShippingDate) : undefined
        this.pushToStockDate = dto.pushToStockDate ? dayjs(dto.pushToStockDate) : undefined
        this.completeDate = dto.completeDate ? dayjs.utc(dto.completeDate) : undefined
        this.completionReportUri = dto.completionReportUri

        this.locations = dto.locations?.map(requestLocationDto => new RequestLocation(requestLocationDto)) || []

        this.transports = dto.transports?.map((transportDto, index) => new ShippingOrderTransport({
            dto: transportDto,
            sizeUnits,
            sequence: index,
            locations: this.locations,
        })) || []
        this.assets = dto.assets?.map(asset => new ShippingOrderAsset({
            dto: asset,
            forwardToShippingDate: this.forwardToShippingDate,
            pushToStockDate: this.pushToStockDate,
            departureLocation: this.locations.find(l => l.id === asset.departureLocationId)!,
            arrivalLocation: this.locations.find(l => l.id === asset.arrivalLocationId)!,
        })) || []
        this.eventLocation = this.locations.find(l => l.type === RequestLocationType.eventLocation)
        this.deliveryNoteHeaderText = dto.deliveryNoteHeaderText ?? undefined
        this.deliveryNoteFooterText = dto.deliveryNoteFooterText ?? undefined
        this.deliveryNotePrintFeatures = dto.deliveryNotePrintFeatures

        this.linkDelete = dto.links.find(link => link.entity === ApiLinkEntity.shippingOrder && link.rel === ApiLinkRel.delete)
    }

    static getTypeLabel(type: ShippingOrderType) {
        return i18n.t(`shippingOrderType.${type}`)
    }

    areAllTransportAssetsReleasedOrStored(transportId: Guid): boolean {
        return !this.assets.some(a => a.transportId === transportId && !a.isReleasedOrStored)
    }

    get departure() {
        // temporary solution - take first asset loading location as SO loading location
        const asset = this.assets[0]
        return this.assets[0]
            ? {
                description: asset.departureDescription,
                coordinates: asset.departureCoordinates,
                address: asset.departureAddress,
            } : undefined
    }

    get arrival() {
        // temporary solution - take first asset unloading location as SO unloading location
        const asset = this.assets[0]
        return this.assets[0]
            ? {
                description: asset.arrivalDescription,
                coordinates: asset.arrivalCoordinates,
                address: asset.arrivalAddress,
            } : undefined
    }

    // check whether a transport segment has stored (in case of delivery SO)
    // or released (in case of return SO) assets
    hasTransportSegmentLoadedAssets({
        transportId,
        departureLocationId,
        arrivalLocationId,
    }: IHasTransportSegmentLoadedAssetsParams) {
        return this.assets.filter(a =>
            a.transportId === transportId
            && a.departureLocationId === departureLocationId
            && a.arrivalLocationId === arrivalLocationId
            && (this.type === ShippingOrderType.return ? a.isStored : a.isReleased)
        ).length > 0
    }
}

export function getShippingOrderDisplayStatusLabel(status: ShippingOrderDisplayStatus) {
    return {
        [ShippingOrderDisplayStatus.loadingListNotCreated]: i18n.t("shippingOrders.displayStatus.loadingListNotCreated"),
        [ShippingOrderDisplayStatus.loadingListInProgress]: i18n.t("shippingOrders.displayStatus.loadingListInProgress"),
        [ShippingOrderDisplayStatus.pushedToStock]: i18n.t("shippingOrders.displayStatus.pushedToStock"),
        [ShippingOrderDisplayStatus.completed]: i18n.t("shippingOrders.displayStatus.closed"),
    }[status] || status
}

export function getShippingOrderDisplayStatusClass(status: ShippingOrderDisplayStatus) {
    switch (status) {
        case ShippingOrderDisplayStatus.loadingListNotCreated:
            return "bg-red-400"
        case ShippingOrderDisplayStatus.loadingListInProgress:
            return "bg-grey-400"
        case ShippingOrderDisplayStatus.pushedToStock:
            return "bg-yellow-500"
        case ShippingOrderDisplayStatus.completed:
            return "bg-green-400"
        default:
            return ""
    }
}
